import { Reducer } from 'redux';
import { set, cloneDeep } from 'lodash-es';
import dayjs from 'dayjs';
import { BodyPart, EncodingType, Template, TemplateScope, TemplateType } from '@dev/ee-api/ee-api-models';
import { EditorColor, EditorTemplateStatus, EditorTemplateVersion } from '@dev/template-editor';
import {
    TemplateEditorActionTypes,
    SetSendTestDataAction,
    TemplateEditorState,
    UploadEditorFileSuccessAction,
    SetBodyPartAction,
    SetDefaultOptionsAction,
    AddTagAction,
    RemoveTagAction,
    SaveStartAction,
    SaveErrorAction,
    LoadSuccessAction,
    SetDefaultTemplateAction,
    SetTemplatePreviewHtmlAction,
    EditorMetaData,
    EditorMetaDataSimple,
    OpenUpsellingOverlayAction,
} from './types';

const defaultState: TemplateEditorState = {
    status: EditorTemplateStatus.DEFAULT,
    isNewTemplate: null,
    sendTest: {
        toEmails: '',
        fromEmail: '',
        fromName: '',
        subject: '',
        errors: null,
        editorData: null,
    },
    files: {
        filesList: [],
        isFileUploading: false,
    },
    autosaveCounter: 0,
    templatePreview: {
        templateHtml: '',
    },
    recentColors: [],
    openUpsellingFeature: null,
};

const generateNewTemplateName = (name?: string): string =>
    `${name ?? 'New template'}: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`;

const setSendTestData = (state: TemplateEditorState, action: SetSendTestDataAction): TemplateEditorState => ({
    ...state,
    sendTest: action.payload,
});

const uploadEditorFileStart = (state: TemplateEditorState): TemplateEditorState => ({
    ...state,
    files: {
        ...state.files,
        isFileUploading: true,
    },
});

const uploadEditorFileSuccess = (
    state: TemplateEditorState,
    action: UploadEditorFileSuccessAction
): TemplateEditorState => ({
    ...state,
    files: {
        filesList: action.payload.filesList,
        isFileUploading: false,
    },
});

const uploadEditorFileError = (state: TemplateEditorState): TemplateEditorState => ({
    ...state,
    files: {
        ...state.files,
        isFileUploading: false,
    },
});

const modifyTemplateObjectBodyPart = (templateObject: Template, contentType: string, content: string): Template => {
    const newBodyPart: BodyPart = {
        ContentType: contentType,
        Content: content,
        Charset: null,
        Encoding: EncodingType.None,
    };
    const index = templateObject?.Body?.Parts?.findIndex(
        (bodyPart: BodyPart): boolean => bodyPart.ContentType === contentType
    );
    const indexToSet = index > -1 ? index : templateObject?.Body?.Parts?.length ?? 0;
    return {
        ...templateObject,
        Body: set(cloneDeep(templateObject?.Body ?? {}), `Parts[${indexToSet}]`, newBodyPart),
    };
};

const modifyTemplateObjectEditorMetaData = (
    templateObject: Template,
    {
        contentJson,
        recentColors,
        screenshot,
    }: { contentJson?: EditorTemplateVersion; recentColors?: EditorColor[]; screenshot?: string }
): Template => {
    const data: EditorMetaData = { data: { contentJson, recentColors: recentColors ?? [] } };
    return {
        ...templateObject,
        Base64Image: screenshot ?? templateObject.Base64Image,
        EditorMetaData: Buffer.from(JSON.stringify(data)).toString('base64') ?? templateObject.EditorMetaData,
    };
};

const modifyTemplateObjectName = (templateObject: Template, name?: string): Template => ({
    ...templateObject,
    Name: name ?? templateObject.Name,
});

const modifyTemplateObjectScope = (templateObject: Template, scope?: TemplateScope): Template => ({
    ...templateObject,
    TemplateScope: scope ?? templateObject.TemplateScope,
});

const setBodyPart = (state: TemplateEditorState, action: SetBodyPartAction): TemplateEditorState => ({
    ...state,
    status: EditorTemplateStatus.MODIFIED,
    templateObject: modifyTemplateObjectBodyPart(
        state.templateObject ?? {},
        action.payload.contentType,
        action.payload.content
    ),
});

const setDefaultOptions = (state: TemplateEditorState, action: SetDefaultOptionsAction): TemplateEditorState => {
    const { fromEmail, fromName, subject } = action.payload;
    return {
        ...state,
        status: EditorTemplateStatus.MODIFIED,
        templateObject: {
            ...state.templateObject,
            FromEmail: fromEmail ?? state.templateObject?.FromEmail,
            FromName: fromName ?? state.templateObject?.FromName,
            Body: {
                ...state.templateObject?.Body,
                Subject: subject ?? state.templateObject?.Body?.Subject,
            },
        },
    };
};

const addTag = (state: TemplateEditorState, action: AddTagAction): TemplateEditorState => {
    const { tag } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);
    const Tags = clonedTemplateObject?.Tags ? [...clonedTemplateObject.Tags, tag] : [tag];
    return {
        ...state,
        status: EditorTemplateStatus.MODIFIED,
        templateObject: { ...clonedTemplateObject, Tags },
    };
};

const removeTag = (state: TemplateEditorState, action: RemoveTagAction): TemplateEditorState => {
    const { tag } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);
    const Tags = state.templateObject?.Tags?.filter((o) => o !== tag) ?? [];

    return {
        ...state,
        status: EditorTemplateStatus.MODIFIED,
        templateObject: { ...clonedTemplateObject, Tags },
    };
};

const setDefaultTemplate = (state: TemplateEditorState, action: SetDefaultTemplateAction): TemplateEditorState => {
    const { templateObject } = action.payload;
    const templateName = generateNewTemplateName();

    const metaData: EditorMetaData | EditorMetaDataSimple | null = templateObject?.EditorMetaData
        ? JSON.parse(Buffer.from(templateObject.EditorMetaData, 'base64').toString('utf8'))
        : null;

    return {
        ...state,
        templateLoadedJson: (metaData as EditorMetaData)?.data?.contentJson ?? metaData as EditorMetaDataSimple ?? null,
        status: EditorTemplateStatus.LOADED,
        isNewTemplate: true,
        templateName,
        templateObject: {
            ...templateObject,
            DateAdded: dayjs().format(),
            Name: templateName,
            IsDefault: false,
            TemplateScope: TemplateScope.Private,
            TemplateType: TemplateType.TemplateEditor,
        },
    };
};

const setTemplatePreviewHtml = (
    state: TemplateEditorState,
    action: SetTemplatePreviewHtmlAction
): TemplateEditorState => {
    const { templateHtml } = action.payload;

    return {
        ...state,
        templatePreview: {
            templateHtml,
        },
    };
};

const incrementAutosaveCounter = (state: TemplateEditorState): TemplateEditorState => ({
    ...state,
    autosaveCounter: 1 + state.autosaveCounter,
});

const saveStart = (state: TemplateEditorState, action: SaveStartAction): TemplateEditorState => {
    const { isDraftTemplate } = action.payload;
    const { name, content, contentJson, screenshot, recentColors } = action.payload.data;
    const templateObjectWithNewBody = modifyTemplateObjectBodyPart(state.templateObject, 'text/html', content);
    const templateObjectWithNewName = modifyTemplateObjectName(templateObjectWithNewBody, name);
    const templateObjectWithNewScope = modifyTemplateObjectScope(
        templateObjectWithNewName,
        isDraftTemplate ? TemplateScope.Draft : null
    );
    const newTemplateObject = modifyTemplateObjectEditorMetaData(templateObjectWithNewScope, {
        contentJson,
        recentColors,
        screenshot,
    });
    return {
        ...state,
        templateObject: newTemplateObject,
        status: EditorTemplateStatus.SAVING,
        recentColors,
        nameError: null,
    };
};

const saveSuccess = (state: TemplateEditorState): TemplateEditorState => ({
    ...state,
    isNewTemplate: false,
    status: EditorTemplateStatus.SAVED,
    templateName: state.templateObject.Name,
});

const saveError = (state: TemplateEditorState, action: SaveErrorAction): TemplateEditorState => ({
    ...state,
    status: EditorTemplateStatus.SAVE_ERROR,
    nameError: action.payload.nameError,
});

const loadStart = (state: TemplateEditorState): TemplateEditorState => ({
    ...state,
    status: EditorTemplateStatus.LOADING,
});

const loadSuccess = (state: TemplateEditorState, action: LoadSuccessAction): TemplateEditorState => {
    const { templateObject, isPublic, useDraftScope } = action.payload;
    const forceDraftScope = useDraftScope && templateObject.TemplateScope !== TemplateScope.Draft;
    const templateName =
        templateObject.IsPublic || forceDraftScope ? generateNewTemplateName(templateObject.Name) : templateObject.Name;

    const metaData: EditorMetaData | EditorMetaDataSimple | null = templateObject.EditorMetaData
        ? JSON.parse(Buffer.from(templateObject.EditorMetaData, 'base64').toString('utf8'))
        : null;
    return {
        ...state,
        templateLoadedJson: (metaData as EditorMetaData)?.data?.contentJson ?? metaData as EditorMetaDataSimple ?? null,
        recentColors: (metaData as EditorMetaData)?.data?.recentColors ?? null,
        templateName,
        isNewTemplate: templateObject.IsPublic || forceDraftScope,
        status: EditorTemplateStatus.LOADED,
        templateObject: {
            ...templateObject,
            IsPublic: isPublic || forceDraftScope ? false : templateObject.IsPublic,
            TemplateScope:
                (forceDraftScope && TemplateScope.Draft) || isPublic
                    ? TemplateScope.Private
                    : templateObject.TemplateScope,
            Name: templateName,
        },
    };
};

const loadError = (state: TemplateEditorState): TemplateEditorState => ({
    ...state,
    status: EditorTemplateStatus.LOAD_ERROR,
});

const openUpsellingOverlay = (state: TemplateEditorState, action: OpenUpsellingOverlayAction): TemplateEditorState => ({
    ...state,
    openUpsellingFeature: action.payload.feature,
});

const closeUpsellingOverlay = (state: TemplateEditorState): TemplateEditorState => ({
    ...state,
    openUpsellingFeature: null,
});

const reducer: Reducer<TemplateEditorState> = (
    state: TemplateEditorState = defaultState,
    action
): TemplateEditorState => {
    switch (action.type) {
        case TemplateEditorActionTypes.SET_SEND_TEST_DATA:
            return setSendTestData(state, action as SetSendTestDataAction);
        case TemplateEditorActionTypes.UPLOAD_FILE_START:
            return uploadEditorFileStart(state);
        case TemplateEditorActionTypes.UPLOAD_FILE_SUCCESS:
            return uploadEditorFileSuccess(state, action as UploadEditorFileSuccessAction);
        case TemplateEditorActionTypes.UPLOAD_FILE_ERROR:
            return uploadEditorFileError(state);
        case TemplateEditorActionTypes.SET_BODY_PART:
            return setBodyPart(state, action as SetBodyPartAction);
        case TemplateEditorActionTypes.SET_DEFAULT_OPTIONS:
            return setDefaultOptions(state, action as SetDefaultOptionsAction);
        case TemplateEditorActionTypes.ADD_TAG:
            return addTag(state, action as AddTagAction);
        case TemplateEditorActionTypes.REMOVE_TAG:
            return removeTag(state, action as RemoveTagAction);
        case TemplateEditorActionTypes.SET_DEFAULT_TEMPLATE:
            return setDefaultTemplate(state, action as SetDefaultTemplateAction);
        case TemplateEditorActionTypes.INCREMENT_AUTOSAVE_COUNTER:
            return incrementAutosaveCounter(state);
        case TemplateEditorActionTypes.SAVE_START:
            return saveStart(state, action as SaveStartAction);
        case TemplateEditorActionTypes.SAVE_SUCCESS:
            return saveSuccess(state);
        case TemplateEditorActionTypes.SAVE_ERROR:
            return saveError(state, action as SaveErrorAction);
        case TemplateEditorActionTypes.LOAD_START:
            return loadStart(state);
        case TemplateEditorActionTypes.LOAD_SUCCESS:
            return loadSuccess(state, action as LoadSuccessAction);
        case TemplateEditorActionTypes.LOAD_ERROR:
            return loadError(state);
        case TemplateEditorActionTypes.SET_TEMPLATE_PREVIEW_HTML:
            return setTemplatePreviewHtml(state, action as SetTemplatePreviewHtmlAction);
        case TemplateEditorActionTypes.OPEN_UPSELLING_OVERLAY:
            return openUpsellingOverlay(state, action as OpenUpsellingOverlayAction);
        case TemplateEditorActionTypes.CLOSE_UPSELLING_OVERLAY:
            return closeUpsellingOverlay(state);
        case TemplateEditorActionTypes.RESET:
            return defaultState;
        default:
            return state;
    }
};

export { reducer as templateEditor };

export default reducer;
